import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Headphones, PlayCircle, BookOpen, Heart } from "lucide-react";
import { api } from "@/lib/api";

export default function ModuleView() {
  const { moduleId } = useParams();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/program/modules/${moduleId}`);
        if (!cancelled) setModule(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Module not available.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [moduleId]);

  return (
    <div className="min-h-screen bg-cream" data-testid="page-module">
      <header className="bg-cream-soft border-b border-hairline sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-ink-soft hover:text-ink text-sm" data-testid="module-back">
            <ArrowLeft size={14} strokeWidth={1.5} /> Back to dashboard
          </Link>
          <Link to="/" className="font-serif-display text-xl text-ink">
            Pip<span className="text-gold italic"> Bennett</span>
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
        {loading && <div className="text-ink-soft">Loading…</div>}
        {error && <div className="text-red-700">{error}</div>}
        {module && (
          <>
            {/* Hero */}
            <div className="eyebrow mb-4 text-sage">{module.subtitle}</div>
            <h1 className="font-serif-display text-4xl md:text-6xl text-ink leading-[1.03]" data-testid="module-title">
              {module.title}
            </h1>
            <p className="mt-6 text-ink-soft max-w-2xl leading-relaxed text-lg">{module.description}</p>
            {module.duration && (
              <div className="mt-6 inline-flex items-center gap-2 text-xs text-ink-soft border border-hairline rounded-full px-4 py-1.5">
                <PlayCircle size={12} strokeWidth={1.5} /> {module.duration}
              </div>
            )}

            {/* Video — appears when video_url is set */}
            {module.video_url ? (
              <div className="mt-12 bg-ink rounded-2xl overflow-hidden aspect-video" data-testid="module-video">
                <video className="w-full h-full" controls src={module.video_url} />
              </div>
            ) : (
              <div className="mt-12 bg-sage/5 border border-sage/20 rounded-2xl p-10 md:p-12 text-center">
                <PlayCircle size={36} strokeWidth={1.3} className="mx-auto mb-4 text-sage" />
                <div className="font-serif-display text-2xl text-ink">Teaching video coming soon.</div>
                <p className="mt-3 text-ink-soft text-sm max-w-md mx-auto">
                  Pip's face-to-camera teaching for this module is in production. The full written teaching is below — take your time with it.
                </p>
              </div>
            )}

            {/* Teaching text */}
            {module.teaching?.length > 0 && (
              <section className="mt-16" data-testid="module-teaching">
                <div className="eyebrow mb-4 text-sage flex items-center gap-2">
                  <Heart size={12} strokeWidth={1.8} /> The Teaching
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight mb-8">A word from Pip.</h2>
                <div className="bg-white border border-hairline rounded-2xl p-8 md:p-12 space-y-6 text-ink leading-relaxed text-[1.02rem]">
                  {module.teaching.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                  <div className="pt-6 mt-4 border-t border-hairline font-serif-display text-xl italic text-sage">
                    — Love, Pip xox
                  </div>
                </div>
              </section>
            )}

            {/* Workbook reflections */}
            {module.workbook?.length > 0 && (
              <section className="mt-16" data-testid="module-workbook-section">
                <div className="eyebrow mb-4 text-sage flex items-center gap-2">
                  <BookOpen size={12} strokeWidth={1.8} /> Your Workbook
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight mb-3">
                  Reflections for this week.
                </h2>
                <p className="text-ink-soft max-w-xl mb-10 leading-relaxed">
                  There's no right or wrong here. Use these as invitations, not tasks. Even one sentence is enough.
                </p>

                <div className="space-y-5">
                  {module.workbook.map((r, i) => (
                    <details
                      key={i}
                      className="group bg-white border border-hairline rounded-2xl p-7 md:p-9 open:shadow-[0_20px_40px_-20px_rgba(74,93,79,0.15)] transition-all"
                      data-testid={`workbook-reflection-${i}`}
                      open={i === 0}
                    >
                      <summary className="cursor-pointer flex items-start justify-between gap-5 list-none">
                        <div className="flex items-start gap-5">
                          <span className="font-serif-display text-3xl md:text-4xl text-gold/80 font-light tabular-nums leading-none flex-shrink-0 w-14">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="font-serif-display text-xl md:text-2xl text-ink leading-tight pt-1">
                            {r.title}
                          </span>
                        </div>
                        <span className="mt-2 text-sage text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                      </summary>
                      <div className="mt-6 ml-0 md:ml-[4.75rem] space-y-5">
                        <p className="text-ink-soft leading-relaxed italic">{r.teaching}</p>
                        <div className="bg-cream-soft border-l-4 border-sage rounded-r-lg p-5">
                          <div className="eyebrow text-sage mb-2">Reflection</div>
                          <p className="text-ink leading-relaxed">{r.prompt}</p>
                        </div>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {/* Workbook PDF download */}
            {module.workbook_url && (
              <section className="mt-16">
                <div className="bg-white border border-hairline rounded-2xl p-8 md:p-10 flex items-center justify-between gap-6 flex-col md:flex-row">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-sage/10 text-sage inline-flex items-center justify-center flex-shrink-0">
                      <FileText size={18} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-serif-display text-xl text-ink">Printable Workbook</h3>
                      <p className="mt-1 text-ink-soft text-sm">A beautifully formatted PDF for your journal time.</p>
                    </div>
                  </div>
                  <a
                    href={module.workbook_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-6 py-3 font-medium text-sm whitespace-nowrap"
                    data-testid="module-workbook-download"
                  >
                    Download PDF
                  </a>
                </div>
              </section>
            )}

            {/* Audio meditations */}
            {(module.audio?.length || 0) > 0 && (
              <section className="mt-16" data-testid="module-meditations">
                <div className="eyebrow mb-4 text-sage flex items-center gap-2">
                  <Headphones size={12} strokeWidth={1.8} /> Your Meditations
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight mb-3">
                  Your bookends for the week.
                </h2>
                <p className="text-ink-soft max-w-xl mb-10 leading-relaxed">
                  Begin with the morning meditation. Close with the evening. Let them become a gentle rhythm you can rely on.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {module.audio.map((a, i) => (
                    <div key={i} className="bg-white border border-hairline rounded-2xl p-7" data-testid={`module-audio-${i}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <Headphones size={18} strokeWidth={1.4} className="text-gold" />
                        <div className="font-serif-display text-xl text-ink">{a.label}</div>
                      </div>
                      {a.url ? (
                        <audio controls src={a.url.startsWith('http') ? a.url : `${process.env.REACT_APP_BACKEND_URL}${a.url}`} className="w-full" />
                      ) : (
                        <div className="text-sm text-ink-soft italic">Pip's voice recording coming soon.</div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Closing */}
            <div className="mt-20 text-center pb-8">
              <div className="font-serif-display text-xl italic text-sage">
                Keep going gently. You're already becoming.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
